import { useState, useEffect } from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Space, Select, Input, Flex, Modal, Popover } from "antd";

import { AimOutlined, FormOutlined, SearchOutlined, DeleteOutlined, DownOutlined,UpOutlined, CopyOutlined  } from '@ant-design/icons';

import { v4 as uuidv4 } from 'uuid';

import "./styles.scss";
import { ActionButton } from "../../../components/elements/Button";

const generateUniqueId = () => uuidv4();

export const InfoCircle = () => (
  <span
    className="custom-info-circle"
    aria-label="info"
  >
    i
  </span>
);

const defaultStepCellStyle = {
    position: "relative",
    padding: "1rem",
    backgroundColor: "#fff",
    border: "1px solid rgba(219, 241, 255, 1)",
    borderRadius: "3px",
   // boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    cursor: "grab",  
};

const stepTypes = {
    "": "Select action type",
    "click_on_element": "Click on element",
    "collect_dl_object": "Collect dataLayer object",
    "collect_dl_before_navigation": "Collect dataLayer before navigation",
    "date_range_select": "Select date range",
    "fill_input_field": "Fill input field",
    "go_to_page": "Go to page",
    "pick_random_element_from_list": "Pick random element from list",
    "select_option": "Select dropdown menu option",
    "wait_until_element_visible": "Wait until element appear",
};

const stepParamDescriptions = {
    "action_type": "Define an action type that has to be executed on the page.",
    "delay": "A time delay after action complete (in seconds).",
    "optional": "Wether current step is optional and should not interrupt the execution (might be useful for elements that appears in unique cases).",
    "wait_for_page_load": "Wait for a page load after completing the action.",
    "opens_new_page": "Move execution to the new page opened by the action.",
    "element_selector": "Query selector of the element.",
    "value": "Fill the input value with following text.",
    "datalayer_obj_to_find": "Template of the DataLayer object to find on the page.",
};


const defaultStepObject = () => ({
    step: null,
    stepId: generateUniqueId(),
    delay: 0,
    type: null,
    selector: null,
    optional: false,
    waitForNavigation: false,
    opensNewPage: false,
    dataLayerObjToFind: null,
    value: null,
    calendarSel: "",
    arrowSel: "",
    daysSel: "",
    availableDaysClass: "",
    minDaysNeeded: 1,
});

// Sortable item component
function SortableItem(props) {
    const {
        step = {},
        index = 0,
        isCollapsed,
        handleDataLayerObjEdit = () => { },
        handleStepEdit = () => { },
        handleDeleteStepClick = () => { },
        handleCopyStepClick = () => { },
        handleToggleStepClick = () => { },
    } = props;

    const [stepId, setStepId] = useState(step?.stepId || "");
    const [stepType, setStepType] = useState(step?.type || "");
    const [stepSelector, setStepSelector] = useState(step?.selector || "");
    const [stepDelay, setStepDelay] = useState(step?.delay || 0);
    const [stepOptional, setStepOptional] = useState(!!step?.optional);
    const [stepWaitForPageLoad, setWaitForPageLoad] = useState(!!step?.waitForNavigation);
    const [stepOpensNewPage, setStepOpensNewPage] = useState(!!step?.opensNewPage);
    const [stepDataLayerObj, setStepDataLayerObj] = useState(step?.dataLayerObjToFind || []);
    const [stepValue, setStepValue] = useState(step?.value);

    const [stepCalendarSel, setStepCalendarSel] = useState(step?.calendarSel || "");
    const [stepArrowSel, setStepArrowSel] = useState(step?.arrowSel || "");
    const [stepDaysSel, setStepDaysSel] = useState(step?.daysSel || "");
    const [stepAvailableDaysClassSel, setStepAvailableDaysClassSel] = useState(step?.availableDaysClass || "");
    const [stepMinDaysNeededSel, setStepMinDaysNeededSel] = useState(step?.minDaysNeeded || 1);

    // Field labels
    const [stepValueLabel, setStepValueLabel] = useState();
    useEffect(() => {
        switch (stepType) {
            case "go_to_page":
                setStepValueLabel("Page address");
                break;
            case "collect_dl_object":
            case "collect_dl_before_navigation":
                setStepValueLabel("Event name");
                break;
            default:
                setStepValueLabel("Input field value");
        }
    }, [stepType]);

    // useSortable hook
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: step.stepId,
        key: step.stepId,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || undefined,
        width: "100%",
        ...defaultStepCellStyle,
    };

    useEffect(() => {
        const newStepData = step;
        step.step = index;
        step.type = stepType;
        step.selector = stepSelector;
        step.delay = stepDelay;
        step.optional = stepOptional;
        step.waitForNavigation = stepWaitForPageLoad;
        step.opensNewPage = stepOpensNewPage;
        step.dataLayerObjToFind = stepDataLayerObj;
        step.value = stepValue;

        step.calendarSel = stepCalendarSel;
        step.arrowSel = stepArrowSel;
        step.daysSel = stepDaysSel;
        step.availableDaysClass = stepAvailableDaysClassSel;
        step.minDaysNeeded = stepMinDaysNeededSel;

        handleStepEdit(stepId, newStepData);
    }, [
        stepType,
        stepSelector,
        stepDelay,
        stepOptional,
        stepWaitForPageLoad,
        stepOpensNewPage,
        stepDataLayerObj,
        stepValue,
        stepCalendarSel,
        stepArrowSel,
        stepDaysSel,
        stepAvailableDaysClassSel,
        stepMinDaysNeededSel,
    ]);

    useEffect(() => {
        setStepId(step?.stepId);
        setStepType(step?.type);
        setStepSelector(step?.selector);
        setStepDelay(step?.delay);
        setStepOptional(step?.optional);
        setWaitForPageLoad(step?.waitForNavigation);
        setStepOpensNewPage(step?.opensNewPage);
        setStepDataLayerObj(step?.dataLayerObjToFind);
        setStepValue(step?.value);
        setStepCalendarSel(step?.calendarSel);
        setStepArrowSel(step?.arrowSel);
        setStepDaysSel(step?.daysSel);
        setStepAvailableDaysClassSel(step?.availableDaysClass);
        setStepMinDaysNeededSel(step?.minDaysNeeded);
    }, [step]);

  return (
    <Flex align="start" style={{ width: '100%' }} gap={38}>
      {/* Step number badge */}
      <div className="scenario-step-number">{index}</div>

      {/* The card */}
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={{...style, flexDirection: "column", width: "100%",}}
      >
        {/* Top‐right icon row */}
        <Space
          size={8}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            zIndex: 2,
          }}
        >
          <CopyOutlined
            onPointerDown={e => e.stopPropagation()}
            onClick={() => handleCopyStepClick(step.stepId)}
            style={{ cursor: 'pointer' }}
          />
          <DeleteOutlined
            onPointerDown={e => e.stopPropagation()}
            onClick={() => handleDeleteStepClick(step.stepId)}
            style={{ cursor: 'pointer' }}
          />
          {isCollapsed ? (
            <DownOutlined
              onPointerDown={e => e.stopPropagation()}
              onClick={() => handleToggleStepClick(step.stepId)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <UpOutlined
              onPointerDown={e => e.stopPropagation()}
              onClick={() => handleToggleStepClick(step.stepId)}
              style={{ cursor: "pointer" }}
            />
          )}
        </Space>

        {/* Main form fields */}
          {/* show the summary header when collapsed */}
        {isCollapsed && (
          <div >
            {stepType ? stepTypes[stepType] : 'No action selected…'}
          </div>
        )}
        {/* Show the full form when not collapsed */}
        {!isCollapsed && (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          {/* Type of action */}
          <Popover placement="topLeft" content={stepParamDescriptions.action_type}>
            <Space size={5} align="center">
              <label className="scenario-step-label">
                Type of (inter) action
              </label>
              <InfoCircle />
            </Space>
          </Popover>
          <Select
            options={Object.keys(stepTypes).map(key => ({
              value: key,
              label: stepTypes[key],
              disabled: key === '',
            }))}
            value={stepType}
            placeholder="Select action type"
            onChange={value => setStepType(value)}
            popupMatchSelectWidth={false}
            onPointerDown={e => e.stopPropagation()}
            style={{ height: 30 }}
          />

          {/* Element selector (for most stepTypes) */}
          {!['collect_dl_object', 'go_to_page', 'date_range_select'].includes(stepType) && (
            <Space size={5} direction="vertical">
              <Popover placement="topLeft" content={stepParamDescriptions.element_selector}>
                <Space size={5} align="center">
                  <label className="scenario-step-label">
                    Element selector
                  </label>
                  <InfoCircle />
                </Space>
              </Popover>
              <Input
                name="step_selector"
                value={stepSelector}
                placeholder="Element selector"
                onChange={e => setStepSelector(e.target.value)}
                onPointerDown={e => e.stopPropagation()}
                style={{ minWidth: 220, height: 30 }}
              />
            </Space>
          )}

          {/* DataLayer object edit (for collect_dl_object / collect_dl_before_navigation) */}
          {['collect_dl_object', 'collect_dl_before_navigation'].includes(stepType) && (
            <Space size={5} direction="vertical">
              <Popover placement="topLeft" content={stepParamDescriptions.datalayer_obj_to_find}>
                <Space size={5} align="center">
                  <SearchOutlined />
                  <label className="scenario-step-label">
                    DataLayer object to find
                  </label>
                </Space>
              </Popover>
              <ActionButton
                type="primary"
                text={<Space size={10}><FormOutlined /> Edit object template</Space>}
                height={30}
                width={200}
                onClickFunction={() => handleDataLayerObjEdit(stepDataLayerObj, stepId)}
              />
            </Space>
          )}

          {/* Date-range selector fields */}
          {stepType === 'date_range_select' && (
            <>
              {[
                { label: 'Calendar element',    value: stepCalendarSel,    setter: setStepCalendarSel,    name: 'step_calendar_selector' },
                { label: 'Arrow element',       value: stepArrowSel,       setter: setStepArrowSel,       name: 'step_calendar_arrow' },
                { label: 'Days',                value: stepDaysSel,        setter: setStepDaysSel,        name: 'step_days_selector' },
                { label: 'Available days',      value: stepAvailableDaysClassSel, setter: setStepAvailableDaysClassSel, name: 'step_available_days_selector' },
              ].map(({ label, value, setter, name }) => (
                <Space key={name} size={5} direction="vertical">
                  <Popover placement="topLeft" content="">
                    <Space size={5} align="center">
                      <AimOutlined />
                      <label className="scenario-step-label">{label}</label>
                    </Space>
                  </Popover>
                  <Input
                    name={name}
                    value={value}
                    placeholder={`${label} selector`}
                    onChange={e => setter(e.target.value)}
                    onPointerDown={e => e.stopPropagation()}
                    style={{ maxWidth: 135, height: 30 }}
                  />
                </Space>
              ))}

              {/* Days to select (numeric) */}
              <Space size={5} direction="vertical">
                <Popover placement="topLeft" content="">
                  <Space size={5} align="center">
                    <AimOutlined />
                    <label className="scenario-step-label">Days to select</label>
                  </Space>
                </Popover>
                <Input
                  name="step_calendar_min_days"
                  value={stepMinDaysNeededSel}
                  type="number"
                  defaultValue={0}
                  max={30}
                  min={0}
                  onChange={e => setStepMinDaysNeededSel(e.target.value)}
                  onPointerDown={e => e.stopPropagation()}
                  style={{ maxWidth: 135, height: 30 }}
                />
              </Space>
            </>
          )}
        </Space>
        )}
      </div>
    </Flex>
  );
}

// Sortable list component
export const SortableList = (props) => {
    const {
      initialItems,
        items = [],
        handleDataLayerObjEdit = () => { },
        handleStepsUpdate = () => { },
    } = props;

    // Initialize state for items
    const [curItems, setCurItems] = useState(items);
    const [stepToDeleteId, setStepToDeleteId] = useState(null);
    // local copy of your steps
    const [steps, setSteps] = useState(initialItems);
    // track which steps are collapsed
    const [collapsedMap, setCollapsedMap] = useState({});

    // Steps list editing
    const [stepRemoveConfirmation, setStepRemoveConfirmation] = useState(false);

    // Delete handler: deletes the clicked step right after it
    const handleDeleteStepClick = (id) => {
        setStepRemoveConfirmation(true);
        setStepToDeleteId(id);
    }
    const handleDeleteStepConfirmation = () => {
        setStepRemoveConfirmation(false);
        handleStepDelete();
    };
    const handleDeleteStepCancel = () => {
        setStepRemoveConfirmation(false);
        setStepToDeleteId(null);
    };

    // Copy handler: duplicates the clicked step right after it
    const handleCopyStepClick = (stepId) => {
      setCurItems((prev) => {
          const idx = prev.findIndex((s) => s.stepId === stepId);
          if (idx === -1) return prev;

          const original = prev[idx];
          const clone = { ...original, stepId: generateUniqueId() };

          return [
            ...prev.slice(0, idx + 1),  // everything up to and including original
            clone,                      // the new copy
            ...prev.slice(idx + 1),     // the rest
          ];
        });
    };

    // Toggle handler: flips collapsed state
    const handleToggleStepClick = (stepId) => {
      setCollapsedMap((prev) => {
        const next = { ...prev, [stepId]: !prev[stepId] };
        return next;
      });
    };

    // Sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handler when drag ends
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active?.id && over?.id && active.id !== over.id) {
      setCurItems(prevItems => {
        const oldIndex = prevItems.findIndex(i => i.stepId === active.id);
        const newIndex = prevItems.findIndex(i => i.stepId === over.id);
        const newOrder = arrayMove(prevItems, oldIndex, newIndex);

        // **Tell the parent about the new order so it “sticks”**
        handleStepsUpdate(newOrder);

        return newOrder;
      });
    }
  };

    const handleStepAdd = () => setCurItems([...curItems, defaultStepObject()]);

    const handleStepEdit = (id, step) => {
        const newItemsList = curItems.map(elem => {
            switch (id === elem.stepId) {
                case true:
                    return step;
                default:
                    return elem;
            }
        });

        handleStepsUpdate(newItemsList);
    };

    const handleStepDelete = () => {
        const newItemsList = curItems.filter(elem => elem.stepId !== stepToDeleteId) || []
        handleStepsUpdate(newItemsList);
    };

    // Updating data
    useEffect(() => setCurItems(items), [items]);

    return (
        <div className="sortable-list-container">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext  items={curItems.map((item) => item.stepId)} strategy={verticalListSortingStrategy}>
                    <Space direction="vertical">
                        {curItems.length ? (curItems?.map((step, idx) => (
                            <div key={step.stepId} size="large" style={{ display: "flex", alignItems: "flex-start", marginBottom: 24, width: "100%",}} >  
                                <SortableItem
                                    key={step.stepId}
                                    index={idx + 1}
                                    step={step}
                                    handleDataLayerObjEdit={handleDataLayerObjEdit}
                                    handleStepEdit={handleStepEdit}
                                    handleCopyStepClick={handleCopyStepClick}
                                    handleDeleteStepClick={handleDeleteStepClick}
                                    handleToggleStepClick={handleToggleStepClick}
                                    isCollapsed={!!collapsedMap[step.stepId]}
                                />
                            </div>
                        ))) : undefined}
                  <Space size="large" style={{ marginTop: 16 }}>
                    <button
                        style={{
                            background: "transparent",
                            color: "#232821",
                            border: "none",
                            fontWeight: 700,
                            fontSize: "13px",
                            padding: "8px 0", 
                            marginLeft: "18px",
                            letterSpacing: "0.1px",
                            position: "relative",
                            cursor: "pointer",
                            outline: "none",
                            boxShadow: "none",
                            lineHeight: 1.2,
                            minWidth: "auto"
                        }}
                        onClick={handleStepAdd}
                        >
                        + add step
                        <span style={{
                            display: "block",
                            height: "3px",
                            background: "#cfff5e",       // Neon green
                            borderRadius: "2px",
                            position: "absolute",
                            left: "-20px",
                            right: "-20px",
                            bottom: 0
                        }} />
                    </button>                                          
                        </Space>
                    </Space>
                </SortableContext>
            </DndContext>

            <Modal
                centered
                open={stepRemoveConfirmation}
                title="Confirm"
                onOk={() => handleDeleteStepConfirmation()}
                onCancel={() => handleDeleteStepCancel()}
                footer={(_, { OkBtn, CancelBtn }) => (
                    <Flex gap={5}>
                        <CancelBtn />
                        <OkBtn />
                    </Flex>
                )}
            >
                <p>Do you want to delete this step?</p>
            </Modal>
        </div>
    );
};
